import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { DateTime } from 'luxon';
import { FiClock } from "react-icons/fi";
import { ReactComponent as BabysFYLabel } from 'babys-first-year/assets/babyyearbookslabel.svg';
import { Text, Box, Flex, Image, Button } from 'rebass';
import { keyframes } from '@emotion/react';
import { useWindowSize } from 'react-use';
import SnoozeBg from './assets/SnoozeBg.png';
import Banner from 'app/payment/Banner';
import Ring from './assets/Ring.png';
import { SelectNonFormikField } from 'app/form/formik/select/index';
import { publicPatchChildJournalSnoozeReminder } from 'child-journals/api';
import { label } from 'members/profile/notification-settings';
import useEntity from 'entities/hooks/use-entity';

const rotate = keyframes`
  0% { transform: translateX(100%); }
  100% { transform: translateX(0);  }
`;

function updateMessage(dateString) {
    const [datePart, timePart] = dateString.split('T'); // Split date and time
    const [year, month, day] = datePart.split('-');  // Get year, month, day from the date part
    const [hours, minutes, seconds] = timePart.split(':');

    const [sec] = seconds.split('.');
    const time = [hours, minutes, sec].join(':')
    const date = new Date(`${year}-${month}-${day}T${time}`); // This doesn't apply any timezone offset
    date.setHours(hours, minutes, sec, 0); // Set the time portion without considering timezones

    const options = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };
    let formattedDate = date.toLocaleDateString('en-US', options);

    const now = DateTime.utc().set({ hour: hours, minute: minutes, second: sec, millisecond: 0 });
    const formattedTime = now.toFormat("h:mm a"); // Time with AM/PM

    const dayS = date.getDate();
    const suffix = dayS === 1 || dayS === 21 || dayS === 31 ? 'st' :
        dayS === 2 || dayS === 22 ? 'nd' :
            dayS === 3 || dayS === 23 ? 'rd' : 'th';
    formattedDate = formattedDate.replace(/\d+/, dayS + suffix);

    return `Reminder snoozed until ${formattedTime} ${formattedDate}`;
}

function updateSnoozeReminder(selectedValue, timezone) {
    const currentTime = new Date(); // Get current time
    let newTime = new Date(currentTime); // Make a copy of the current time

    // Adding the time based on selected snooze duration
    switch (selectedValue) {
        case '30 minutes':
            newTime.setMinutes(currentTime.getMinutes() + 30);
            break;
        case '1 hour':
            newTime.setHours(currentTime.getHours() + 1);
            break;
        case '2 hours':
            newTime.setHours(currentTime.getHours() + 2);
            break;
        case '1 day':
            newTime.setDate(currentTime.getDate() + 1);
            break;
        default:
            break;
    }

    // Format the time into a readable string (e.g., 7:30 PM or 7:30 PM tomorrow)
    const options = { hour: '2-digit', minute: '2-digit', hour12: true, timezone };
    const formattedTime = newTime.toLocaleTimeString([], options);
    const isTomorrow = selectedValue === '1 day' ? ' tomorrow' : '';

    // Update the text
    const reminderText = `Snooze reminder until ${formattedTime}${isTomorrow}?`;

    return reminderText;
}

const howLongOptions = [
    { label: '30 minutes', value: '30 minutes' },
    { label: '1 hour', value: '1 hour' },
    { label: '2 hours', value: '2 hours' },
    { label: 'Tomorrow (same time)', value: '1 day' },
]

const SnoozeReminders = ({ child_journal_id, secret, onStepUp }) => {
    const { entity: childJournal } = useEntity({ type: 'child-journals', id: child_journal_id });
    const { entity: reminderDetails } = useEntity({ type: 'reminders', id: childJournal?.reminder?.id });
    const [status, setStatus] = useState({ type: '', message: '' });
    const [animationStyle, setAnimationStyle] = useState({});
    const { width } = useWindowSize();

    const animateElement = () => {
        setAnimationStyle((prevStyle) => ({
            ...prevStyle,
            opacity: 0,
            animation: 'unset',
        }));

        setTimeout(() => {
            setAnimationStyle((prevStyle) => ({
                ...prevStyle,
                opacity: 1,
                animation: `${rotate} 1s ease`,
            }));
        }, 500);
    };

    useEffect(() => {
        animateElement()
    }, [])

    function setInitialValues() {
        return {
            'snooze-settings': '',
        }
    }

    function setShowValidationSchema() {
        return Yup.object().shape({
            'snooze-settings': Yup.string(),
        })
    }

    const onSubmit = async (values, { setSubmitting }) => {
        setStatus({ type: '', message: '' })

        const data = {
            id: reminderDetails?.id,
            type: "reminders",
            attributes: {
                "snooze-settings": values["snooze-settings"] // Default snooze unit if not provided
            }
        }

        setSubmitting(true)
        const resp = await publicPatchChildJournalSnoozeReminder(secret, data)
        if (resp.status === 200) {
            const runAt = resp?.data?.initial?.data?.attributes?.['run-at']
            const timeZone = resp?.data?.initial?.data?.attributes?.['schedule-time-zone']
            setStatus({ type: 'success', message: updateMessage(runAt, timeZone) });
        }
        else {
            setStatus({ type: 'error', message: "Something went wrong" });
        }
        setSubmitting(false)
    }

    return (
        <Flex bg="white" width="100%" flexDirection={width < 600 ? 'column' : 'row'} height={'100vh'} sx={{ position: width < 600 ? 'unset' : 'fixed' }}>
            {width > 600 && <Flex bg="#EBFAFF" width={["100%", "30%", "30%"]} minWidth={400} >
                <Image src={SnoozeBg} mx="auto" minHeight={500} />
            </Flex>}
            <Flex
                sx={animationStyle}
                maxWidth={500}
                mx="auto"
                flexDirection="column"
                width={width < 600 ? "100%" : "calc(100vw - 30%)"}
                px={2}
                bg="white"
            >
                <Box my={3} mx="auto">
                    <BabysFYLabel />
                </Box>
                {!!status.message && (
                    <Banner
                        bg={status.type === 'success' ? 'successBg' : 'pinks.1'}
                        color={status.type === 'success' ? 'success' : 'pinks.5'}
                        heading={status.message}
                        text={status.subTitle}
                        p="16px"
                        size={'small'}
                    />
                )}
                <Formik
                    initialValues={setInitialValues()}
                    enableReinitialize
                    validationSchema={setShowValidationSchema()}
                    onSubmit={onSubmit}
                >
                    {({ values, isSubmitting, setFieldValue }) => (
                        <Form>
                            <Flex flexDirection="column" >
                                <Text
                                    mt="50px"
                                    color="codGrey"
                                    fontFamily="Poppins"
                                    fontSize={22}
                                    lineHeight="165%"
                                    letterSpacing="0.22"
                                    mb={1}
                                >
                                    Snooze Reminder
                                </Text>
                                <Box sx={{ border: "0.5px solid #E2E8F0" }} my={4} />
                                <Box my={2}>
                                    <SelectNonFormikField
                                        disabled={isSubmitting}
                                        label={label('For how long?')}
                                        options={howLongOptions}
                                        value={values?.['snooze-settings']}
                                        onChange={(option) => {

                                            setStatus({ type: '', message: '' })
                                            setFieldValue('snooze-settings', option.value)
                                            // onSubmit({ 'snooze-settings': option.value })
                                        }}
                                        name="snooze-settings"
                                        width="100%"
                                    />
                                </Box>
                                <Flex flexDirection="column" alignItems="center" mt={5}>
                                    {values?.['snooze-settings'] && !status.message && <Text
                                        sx={{
                                            color: "#000",
                                            textAlign: "center",
                                            fontFamily: "Poppins",
                                            fontSize: "22px",
                                            fontStyle: "normal",
                                            fontWeight: "400",
                                            lineHeight: "165%",
                                            letterSpacing: "0.22px",
                                        }}
                                    >
                                        {updateSnoozeReminder(values?.['snooze-settings'], reminderDetails?.['schedule-time-zone'], reminderDetails?.['run-at'])}
                                    </Text>}

                                    <Button
                                        mt={3}
                                        height={40}
                                        py={1}
                                        px={5}
                                        bg={!values?.['snooze-settings'] || isSubmitting || !!status.message ? "grey" : "#9B59B6"}
                                        type="submit"
                                        disabled={!values?.['snooze-settings'] || isSubmitting || !!status.message}
                                        sx={{ border: 'transparent', fontWeight: 500, borderRadius: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center', mx: 'auto' }}
                                    >
                                        <Text as={FiClock} />&nbsp; Confirm Snooze
                                    </Button>
                                    <Button
                                        mt={3}
                                        height={40}
                                        py={1}
                                        px={5}
                                        bg="rgba(155, 89, 182, 0.05)"
                                        onClick={onStepUp}
                                        sx={{ border: '1px solid #9B59B6', fontWeight: 500, borderRadius: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center', mx: 'auto' }}
                                    >
                                        <Image src={Ring} height={30} />&nbsp; <Text color="#9B59B6">Edit Reminders</Text>
                                    </Button>
                                </Flex>
                            </Flex>
                        </Form>
                    )}
                </Formik>
            </Flex>
        </Flex >
    )
};

export default SnoozeReminders;
